/**
 * @file        src/pages/hotel/B2BMarkupList.jsx
 * @description B2B Hotel Markup (CRUD) over margin_rules (hotel-scoped),
 *              audience=B2B. Every rule saved here is stamped level='user',
 *              user_type='3', scoped to a storefront brand — separate from the
 *              B2C Hotel Markup screen.
 * @author      Kinetika Labs
 * @created     2026-07-23
 * @changelog
 *   - 2026-10-07 Claude — Same fields as the B2C Hotel Markup form (the
 *                          reference): Price Min / Price Max added and required
 *                          (Max > Min), labels "Markup Type" (Fix / Percent) and
 *                          "Markup Value (per room / night)", Brand uses the
 *                          shared brand picker. The API applies the B2C overlap
 *                          check to a banded B2B rule, within the B2B scope.
 *   - 2026-10-06 Claude — Domestic / International stays (`dom_int`, judged on
 *                          the property's country — migration 076).
 *   - 2026-10-05 Claude — The amount is labelled PER ROOM / NIGHT: the booking
 *                          engine now charges it × rooms × nights (client
 *                          decision; booking_backend utils/hotelStay.js).
 *   - 2026-08-03 Claude — Brand options come from the tenant's brand registry
 *     (useBrands) instead of a hardcoded Neon/Beyond pair; no default brand —
 *     the form starts blank and brand is required (the backend requires it).
 *   - 2026-08-04 Claude — Agent retirement (CR E8): reworded the subtitle
 *                          ("agent & corporate" -> "corporate"). No change to
 *                          markup fields, computation, or route.
 *   - 2026-09-12 Claude — Precedence notice. A B2B hotel rule carries no price
 *                          band, so it was exempt from the B2C band-overlap check
 *                          and two active rules for the same brand + company could
 *                          both exist. The API now rejects that duplicate.
 *   - 2026-09-15 Claude — Amount min is 0.01 — the route rejects 0, so the form now does too.
 *   - 2026-09-15 Claude — Note now also states that the markup is already inside
 *                          the displayed price, so the convenience fee, discounts
 *                          and coupons are all computed on the marked-up total —
 *                          never on the supplier net.
 */
import SimpleLookupPage from '../package/SimpleLookupPage';
import { MARKUP_BASE_NOTE } from '../../lib/pricingNotes';
import { domIntField } from '../../lib/domInt';

// Labels match the B2C form (AddMarkup.jsx).
const MARKUP_TYPE = [
  { value: 'fixed', label: 'Fix' },
  { value: 'percentage', label: 'Percent' },
];

function validate(form) {
  const min = Number(form.price_min);
  const max = Number(form.price_max);
  if (form.price_min !== '' && form.price_max !== '' && !(max > min)) {
    return { price_max: 'Price Max must be greater than Price Min.' };
  }
  return null;
}

export default function B2BHotelMarkupList() {
  return (
    <SimpleLookupPage
      title="B2B Hotel Markup"
      subtitle="B2B Hotel · corporate markup"
      endpoint="/hotel-markups"
      queryRoot="hotel-markups-b2b"
      addLabel="Add B2B Hotel Markup"
      entityLabel="Markup"
      listParams={{ audience: 'b2b' }}
      notice={<span>Only <b>one</b> markup applies to a stay: a rule pinned to a company beats a general corporate rule; within that, the rule whose price range contains the stay&apos;s price, then a Domestic- or International-only rule beats one for both (a stay is domestic when the hotel is in India); the most recently updated wins a tie. A range that <b>overlaps</b> another active corporate rule on the same brand and Domestic/International choice is rejected — edit the existing rule instead.{MARKUP_BASE_NOTE}</span>}
      fixedValues={{ level: 'user', user_type: '3' }}
      validate={validate}
      fields={[
        { key: 'brand', label: 'Brand', type: 'brand', required: true },
        { key: 'margin_type', label: 'Markup Type', type: 'select', required: true, default: 'fixed', options: MARKUP_TYPE },
        { key: 'margin_value', label: 'Markup Value (per room / night)', type: 'number', required: true, min: 0.01, step: '0.01', placeholder: 'e.g. 250 or 5' },
        { key: 'price_min', label: 'Price Min', type: 'number', required: true, min: 0, step: '0.01', placeholder: 'Applies from this booking amount' },
        { key: 'price_max', label: 'Price Max', type: 'number', required: true, min: 0, step: '0.01', placeholder: 'Applies up to this booking amount' },
        // Domestic / International stays (blank = both), by the property's country.
        domIntField(),
      ]}
    />
  );
}
